import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Instagram, Facebook, Youtube } from 'lucide-react';
import { URL_GOOGLE_PLAY } from '@/components/constants/app';

const Footer: React.FC = () => {
  const { t } = useTranslation();

  return (
    <footer className="bg-[#f7f7f5] dark:bg-black border-t border-gray-300 dark:border-white/10 pt-14 pb-8 text-sm text-gray-700 dark:text-gray-300">
      <div className="w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 px-6 sm:px-0 md:pl-[10%] md:pr-[10%] lg:pl-[15%] lg:pr-[15%] items-start">
        {/* BOOKOLL */}
        <div>
          <p className="font-semibold mb-3 text-[#103254] dark:text-white">{t('footer.bookoll')}</p>
          <ul className="space-y-2">
            <li>
              <Link to="/about" className="hover:underline">
                {t('footer.aboutUs')}
              </Link>
            </li>
            <li>
              <Link to="/pricing" className="hover:underline">
                {t('footer.pricing')}
              </Link>
            </li>
            <li>
              <Link to="/contact" className="hover:underline">
                {t('footer.contact')}
              </Link>
            </li>
            <li>
              <Link to="/faq" className="hover:underline">
                {t('footer.faq')}
              </Link>
            </li>
          </ul>
        </div>

        {/* LEGAL */}
        <div>
          <p className="font-semibold mb-3 text-[#103254] dark:text-white">{t('footer.legal')}</p>
          <ul className="space-y-2">
            <li>
              <Link to="/terms" className="hover:underline">
                {t('footer.terms')}
              </Link>
            </li>
            <li>
              <Link to="/privacy-policy" className="hover:underline">
                {t('footer.privacy')}
              </Link>
            </li>
            <li>
              <Link to="/cookies" className="hover:underline">
                {t('footer.cookies')}
              </Link>
            </li>
            <li>
              <Link to="/delete-profile" className="hover:underline">
                {t('footer.deleteProfile')}
              </Link>
            </li>
          </ul>
        </div>

        {/* REDES */}
        <div>
          <p className="font-semibold mb-3 text-[#103254] dark:text-white">{t('footer.followUs')}</p>

          <ul className="space-y-2 text-gray-600 dark:text-gray-400">
            <li>
              <a
                href="https://www.instagram.com/bookollapp/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 hover:text-black dark:hover:text-white"
              >
                <Instagram className="w-5 h-5" />
                Instagram
              </a>
            </li>

            <li>
              <a
                href="https://www.tiktok.com/@bookollapp?_r=1&_t=ZN-93instoPZ4Q"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 hover:text-black dark:hover:text-white"
              >
                <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24">
                  <path d="M21 8.063a6.554 6.554 0 0 1-3.77-1.19 6.591 6.591 0 0 1-2.43-3.187v9.404a5.78 5.78 0 1 1-5.78-5.78c.2 0 .397.01.592.03v2.948a2.82 2.82 0 1 0 2.23 2.758V2h2.388a4.17 4.17 0 0 0 4.17 4.17V8.06z" />
                </svg>
                TikTok
              </a>
            </li>

            <li>
              <a
                href="https://www.facebook.com/profile.php?id=61580134539901"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 hover:text-black dark:hover:text-white"
              >
                <Facebook className="w-5 h-5" />
                Facebook
              </a>
            </li>

            <li>
              <a
                href="https://www.youtube.com/channel/UCB6-ENX4Whi1gDhk2V60qkg"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 hover:text-black dark:hover:text-white"
              >
                <Youtube className="w-5 h-5" />
                YouTube
              </a>
            </li>
          </ul>
        </div>

        {/* APP */}
        <div className="lg:flex lg:justify-end">
          <div>
            <p className="font-semibold mb-3 text-[#103254] dark:text-white">{t('footer.app')}</p>

            <ul className="space-y-2 text-gray-600 dark:text-gray-400">
              <li>
                <a
                  href={URL_GOOGLE_PLAY}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 hover:text-black dark:hover:text-white"
                >
                  <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M22.018 13.298l-3.919 2.218-3.515-3.493 3.543-3.521 3.891 2.202a1.49 1.49 0 0 1 0 2.594zM1.337.924a1.486 1.486 0 0 0-.112.568v21.017c0 .217.045.419.124.6l11.155-11.087L1.337.924zm12.207 10.065l3.258-3.238L3.45.195a1.466 1.466 0 0 0-1.017-.191l11.111 10.985zm0 2.067l-11.1 11.03c.332.05.685-.006 1.007-.19l13.312-7.516-3.219-3.324z" />
                  </svg>
                  {t('footer.android')}
                </a>
              </li>
              <li>
                <span
                  className="flex items-center gap-2 text-gray-400 dark:text-gray-500 cursor-default select-none"
                  title={t('footer.comingSoon') + ' · ' + t('footer.ios')}
                >
                  <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M16.365 1.43c0 1.14-.417 2.2-1.1 2.98-.74.85-1.94 1.5-3.06 1.42-.13-1.1.44-2.27 1.08-3 .72-.83 1.98-1.44 3.08-1.4zM20.5 17.02c-.55 1.27-.82 1.84-1.53 2.96-.99 1.57-2.39 3.52-4.12 3.53-1.54.02-1.94-1-4.03-.99-2.09.01-2.53 1.01-4.07.99-1.73-.02-3.05-1.78-4.04-3.35-2.77-4.4-3.06-9.56-1.35-12.3 1.21-1.94 3.13-3.08 4.93-3.08 1.83 0 2.98 1 4.5 1 1.47 0 2.37-1 4.49-1 1.6 0 3.3.87 4.51 2.38-3.96 2.17-3.32 7.82.24 9.85z" />
                  </svg>
                  {t('footer.ios')}
                  <span className="text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500">
                    · {t('footer.comingSoon')}
                  </span>
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* BOTTOM BAR */}
      <div className="mt-10 border-t border-gray-300 dark:border-white/10 pt-6 flex justify-between items-center text-xs px-6 sm:px-[15%] text-gray-500 dark:text-gray-500">
        <span>{t('footer.language')}</span>
        <span>© {new Date().getFullYear()} Bookoll</span>
      </div>
    </footer>
  );
};

export default Footer;